import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { ACCOUNTING_API, EINVOICE_API, handleApiError } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';

interface EInvoiceStatusModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string | null;
  companyId: string | null;
  /** Sorgu sonucu backend faturanın durumunu güncellediğinde çağrılır —
   *  liste, sayfa yenilenmeden güncel durumu gösterebilsin. */
  onStatusUpdated?: (info: { invoiceId: string; status: string; officialNumber?: string | null }) => void;
}

const EInvoiceStatusModal: React.FC<EInvoiceStatusModalProps> = ({
  open,
  onClose,
  invoiceId,
  companyId,
  onStatusUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [statusData, setStatusData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Fatura bilgilerini yükle (credential backend'de JWT+SQL'den çözülür)
  useEffect(() => {
    if (open && invoiceId && companyId) {
      loadInvoiceData();
    }
  }, [open, invoiceId, companyId]);

  const loadInvoiceData = async () => {
    try {
      console.log('📋 [E-FATURA STATUS] Fatura yükleniyor:', invoiceId);

      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoiceId!));
      const result = response.data;

      if (!result.success || !result.data) {
        throw new Error('Fatura bulunamadı');
      }

      console.log('✅ [E-FATURA STATUS] Fatura yüklendi:', result.data);
      setInvoiceData(result.data);
    } catch (error: any) {
      console.error('❌ [E-FATURA STATUS] Fatura yükleme hatası:', error);
      setError(error.message || 'Fatura yüklenemedi');
    }
  };

  // Belge durumunu sorgula — YENİ EDM servisi (gts_einvoice_api).
  // Kimlik/credential JWT claim + SQL'den backend tarafında çözülür; frontend yalnızca
  // invoiceId gönderir. Backend ayrıca faturanın EInvoiceStatus alanını günceller.
  const queryInvoiceStatus = async () => {
    if (!invoiceData) {
      setError('Fatura bilgileri henüz yüklenmedi.');
      return;
    }

    if (!invoiceData.eInvoiceUUID && !invoiceData.eInvoiceBelgeOid) {
      setError('Bu fatura henüz e-fatura olarak gönderilmemiş (UUID yok). Önce faturayı gönderin.');
      return;
    }

    setLoading(true);
    setError(null);
    setStatusData(null);

    try {
      console.log('🔍 [E-FATURA STATUS] EDM durum sorgulanıyor...', { invoiceId });

      const response = await einvoiceApi.post(EINVOICE_API.EDM_STATUS, {
        invoiceId: invoiceId!,
        branchId: invoiceData.branchId || invoiceData.sellerBranchId || null,
      });
      const result = response.data;

      console.log('✅ [E-FATURA STATUS] EDM yanıtı:', result);

      if (!result?.success) {
        setError(result?.error || 'Durum sorgulanamadı.');
        return;
      }

      if (!result.found) {
        setError('Entegratör tarafında bu faturaya ait durum kaydı bulunamadı. Belge işleniyor olabilir, biraz sonra tekrar deneyin.');
        return;
      }

      setStatusData(result);

      // Backend, faturanın EInvoiceStatus alanını bu sorguyla güncelledi;
      // listedeki satır da sayfa yenilenmeden aynı duruma çekilsin.
      if (invoiceId && result.status) {
        onStatusUpdated?.({
          invoiceId,
          status: result.status,
          officialNumber: result.officialNumber ?? null,
        });
      }
    } catch (error: any) {
      console.error('❌ [E-FATURA STATUS] İstek hatası:', error, error?.response?.data);
      // Backend 400/500 gövdesinde asıl EDM/sunucu hatası döner (error / errorShort / errorLong).
      const body = error?.response?.data;
      const serverMsg = [body?.errorShort, body?.error].filter(Boolean).join(' — ')
        || body?.errorLong;
      setError(serverMsg || handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  // Modal açıldığında otomatik sorgula (fatura yüklenince yeterli; credential backend'de çözülür)
  useEffect(() => {
    if (open && invoiceData) {
      queryInvoiceStatus();
    }
  }, [open, invoiceData]);

  // EDM teknik durum kodlarını kullanıcı dostu Türkçe duruma çevirir.
  // EDM "SEND - SUCCEED", "WAITING", "PROCESSING", "ERROR", "REJECTED" gibi kodlar döner.
  type StatusSeverity = 'success' | 'info' | 'warning' | 'error';
  const interpretStatus = (
    status?: string | null,
    statusDescription?: string | null,
    gibStatusCode?: number | null,
  ): { label: string; detail: string; severity: StatusSeverity; icon: string } => {
    const raw = `${status ?? ''} ${statusDescription ?? ''}`.toUpperCase();

    const has = (...keys: string[]) => keys.some((k) => raw.includes(k));

    // Red / hata öncelikli
    if (has('REJECT', 'RED', 'REDDEDIL')) {
      return { label: 'Reddedildi', detail: 'Alıcı faturayı reddetti.', severity: 'error', icon: '❌' };
    }
    if (has('ERROR', 'FAIL', 'HATA', 'INVALID')) {
      return { label: 'Hata', detail: 'Gönderim sırasında bir hata oluştu. Detay için faturayı tekrar göndermeyi deneyin.', severity: 'error', icon: '⚠️' };
    }
    if (has('CANCEL', 'IPTAL')) {
      return { label: 'İptal edildi', detail: 'Fatura iptal edilmiş.', severity: 'warning', icon: '🚫' };
    }
    // Kabul (ticari)
    if (has('ACCEPT', 'KABUL', 'APPROVE', 'ONAY')) {
      return { label: 'Kabul edildi', detail: 'Alıcı faturayı kabul etti.', severity: 'success', icon: '✅' };
    }
    // Ticari e-fatura: belge alıcıya iletildi, kabul/red yanıtı bekleniyor.
    // Bu bir hata veya işlem gecikmesi değildir; alıcının 8 gün yanıt süresi vardır.
    if (has('WAIT_APPLICATION_RESPONSE', 'APPLICATION_RESPONSE')) {
      return {
        label: 'Alıcı yanıtı bekleniyor',
        detail: 'Fatura alıcıya başarıyla iletildi. Ticari e-faturada alıcı kabul/red yanıtı verene (veya 8 günlük süre dolana) kadar bu durumda kalır. Bir işlem yapmanız gerekmez.',
        severity: 'info',
        icon: '📬',
      };
    }
    // Başarıyla GİB'e iletildi
    if (has('SUCCEED', 'SUCCESS', 'SENT', 'DELIVER', 'BASARIL') || gibStatusCode === 0) {
      return { label: 'Başarıyla gönderildi', detail: 'Fatura GİB sistemine iletildi. Alıcının onayı/yanıtı beklenebilir.', severity: 'success', icon: '✅' };
    }
    // İşleniyor / sırada
    if (has('WAIT', 'QUEUE', 'PENDING', 'BEKL', 'SIRA')) {
      return { label: 'Bekliyor', detail: 'Fatura işlem sırasında. Kısa süre sonra tekrar sorgulayın.', severity: 'warning', icon: '⏳' };
    }
    if (has('PROCESS', 'SENDING', 'GONDERIL', 'ISLEN')) {
      return { label: 'İşleniyor', detail: 'Fatura GİB’e gönderiliyor. Kısa süre sonra tekrar sorgulayın.', severity: 'info', icon: '🔄' };
    }
    // Bilinmeyen
    return {
      label: status || 'Bilinmiyor',
      detail: statusDescription || 'Durum bilgisi alındı.',
      severity: 'info',
      icon: 'ℹ️',
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>E-Fatura Durum Sorgulama</DialogTitle>
      <DialogContent>
        {/* Fatura Bilgileri */}
        {invoiceData && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              📋 Fatura Bilgileri
            </Typography>
            <Typography variant="body2">
              <strong>Fatura No:</strong> {invoiceData.invoiceNumber}
            </Typography>
            <Typography variant="body2">
              <strong>Cari:</strong> {invoiceData.accountName}
            </Typography>
            <Typography variant="body2">
              <strong>Tutar:</strong> {invoiceData.grandTotal?.toFixed(2)} TL
            </Typography>
            {invoiceData.eInvoiceETTN && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>ETTN:</strong> <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{invoiceData.eInvoiceETTN}</Typography>
              </Typography>
            )}
          </Box>
        )}

        {/* Yükleniyor */}
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="body1">E-fatura durumu sorgulanıyor...</Typography>
          </Box>
        )}

        {/* Hata */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              ❌ Sorgulama Hatası
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          </Alert>
        )}

        {/* Durum Sonucu */}
        {statusData && !loading && !error && (() => {
          const s = interpretStatus(statusData.status, statusData.statusDescription, statusData.gibStatusCode);
          const bg: Record<StatusSeverity, string> = {
            success: '#e8f5e9',
            info: '#e3f2fd',
            warning: '#fff8e1',
            error: '#ffebee',
          };
          return (
            <Box sx={{ mb: 2 }}>
              {/* Büyük, anlaşılır durum kartı */}
              <Paper
                variant="outlined"
                sx={{ p: 2.5, mb: 2, backgroundColor: bg[s.severity], display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <Typography sx={{ fontSize: '2.2rem', lineHeight: 1 }}>{s.icon}</Typography>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {s.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {s.detail}
                  </Typography>
                </Box>
              </Paper>

              {/* Red/başarısızlık gerekçesi (GİB/EDM) — en kritik bilgi, belirgin göster */}
              {(s.severity === 'error' || s.severity === 'warning') && (statusData.reason || statusData.errorMessage) && (
                <Alert severity={s.severity === 'error' ? 'error' : 'warning'} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Gönderim / GİB Gerekçesi
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {statusData.reason || statusData.errorMessage}
                  </Typography>
                  {!statusData.reason && !statusData.gibStatusDescription && !statusData.responseDescription && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Not: EDM/GİB bu fatura için ayrıntılı gerekçe döndürmedi. Faturayı düzenleyip yeniden göndermeyi deneyin.
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Detaylar */}
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
                <Stack spacing={1.5}>
                  {statusData.gibStatusDescription && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        GİB Açıklaması
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {statusData.gibStatusDescription}
                      </Typography>
                    </Box>
                  )}

                  {statusData.responseDescription && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Yanıt Açıklaması
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {statusData.responseDescription}
                        {statusData.responseCode ? ` (${statusData.responseCode})` : ''}
                      </Typography>
                    </Box>
                  )}

                  {statusData.officialNumber && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Resmi Fatura No
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {statusData.officialNumber}
                      </Typography>
                    </Box>
                  )}

                  {statusData.uuid && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        ETTN (UUID)
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {statusData.uuid}
                      </Typography>
                    </Box>
                  )}

                  {statusData.envelopeIdentifier && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Zarf No
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {statusData.envelopeIdentifier}
                      </Typography>
                    </Box>
                  )}

                  {/* Teknik durum kodu (küçük, ikincil) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Teknik durum:
                    </Typography>
                    <Chip label={statusData.status || '—'} size="small" variant="outlined" />
                    {statusData.gibStatusCode != null && (
                      <Chip label={`GİB Kod: ${statusData.gibStatusCode}`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Stack>

                {/* Tam JSON Yanıt (Daraltılabilir) */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
                    🔍 Tam JSON Yanıt (teknik)
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, mt: 0.5, backgroundColor: '#fff', maxHeight: 180, overflow: 'auto', border: '1px dashed #ccc' }}
                  >
                    <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#424242', m: 0 }}>
                      {JSON.stringify(statusData, null, 2)}
                    </Typography>
                  </Paper>
                </Box>
              </Paper>
            </Box>
          );
        })()}

        {/* İlk Yükleme */}
        {!statusData && !loading && !error && invoiceData && (
          <Typography variant="body2" color="text.secondary" align="center">
            Durum sorgulanıyor...
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Kapat
        </Button>
        <Button
          onClick={queryInvoiceStatus}
          variant="outlined"
          color="info"
          disabled={loading || !invoiceData}
        >
          {loading ? 'Sorgulanıyor...' : 'Yeniden Sorgula'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EInvoiceStatusModal;

